import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  CardBodyComponent,
  CardComponent,
  CustomerCardComponent,
  CustomerOpportunityItem,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { OPPORTUNITY_FUNNEL_STEPS } from '../../constants/opportunity-funnel.constants';
import { AddressInfo, CustomerOpportunitiesInfo, CustomerOpportunitySummaryInfo } from '../../sdk/sdk';
import { AddressNavigationService } from '../../services/address-navigation.service';

@Component({
  selector: 'app-customer-opportunities-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CardComponent, CardBodyComponent, CustomerCardComponent, StateMessageComponent],
  templateUrl: './customer-opportunities-card.component.html',
})
export class CustomerOpportunitiesCardComponent {
  private readonly addressNavigation = inject(AddressNavigationService);

  readonly customerOpportunities = input<CustomerOpportunitiesInfo | undefined>(undefined);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);

  readonly state = computed<'loading' | 'error' | 'no-matches' | 'matches'>(() => {
    if (this.isLoadingInput()) {
      return 'loading';
    }
    if (this.hasErrorInput()) {
      return 'error';
    }
    if (!this.customerOpportunities()) {
      return 'no-matches';
    }
    return 'matches';
  });

  readonly customerName = computed(() => this.customerOpportunities()?.customerName ?? '');
  readonly customerIdText = computed(() => this.customerOpportunities()?.customerId?.toString() ?? '');
  readonly customerSinceText = computed(() => formatCardDate(this.customerOpportunities()?.customerSince) ?? '');
  readonly phone = computed(() => formatPhone(this.customerOpportunities()?.phone));
  readonly email = computed(() => this.customerOpportunities()?.email ?? '');
  readonly addressText = computed(() => formatAddress(this.customerOpportunities()?.address));

  readonly opportunityItems = computed<CustomerOpportunityItem[]>(() =>
    (this.customerOpportunities()?.opportunities ?? []).map((o) => toCustomerOpportunityItem(o)),
  );

  getState(): string {
    return this.state();
  }

  onAddressClick(): void {
    this.addressNavigation.openInHomepage(this.customerOpportunities()?.address);
  }
}

type ThemeColor = 'orange' | 'purple' | 'aqua' | 'green-tint';

function themeColorForType(typeName: string | undefined): ThemeColor {
  switch (typeName?.toLowerCase()) {
    case 'recurring':
      return 'orange';
    case 'repeat':
      return 'purple';
    default:
      return 'aqua';
  }
}

function formatAddress(address: AddressInfo | undefined): string {
  if (!address) {
    return '';
  }
  const line = [address.streetAddress, address.city, address.state].filter(Boolean).join(', ');
  return address.postalCode ? `${line} ${address.postalCode}`.trim() : line;
}

export function formatPhone(phone: string | undefined): string {
  if (!phone) {
    return '';
  }
  const digits = phone.replace(/\D/g, '');
  // Drop a leading US country code so 11-digit numbers still format.
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) {
    return phone;
  }
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function formatCardDate(date: Date | undefined): string | undefined {
  return date ? date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : undefined;
}

export function toCustomerOpportunityItem(opportunity: CustomerOpportunitySummaryInfo): CustomerOpportunityItem {
  return {
    typeName: opportunity.opportunityTypeName ?? '',
    themeColor: themeColorForType(opportunity.opportunityTypeName),
    statusBadge: opportunity.isActive ? 'Active' : undefined,
    statusBadgeType: 'success',
    date: formatCardDate(opportunity.createdAt),
    isCommercial: opportunity.isCommercial ?? false,
    commercialToggleReadonly: true,
    isActive: opportunity.isActive,
    isClickable: false,
    steps: OPPORTUNITY_FUNNEL_STEPS,
    currentStep: opportunity.phaseName ?? undefined,
    furthestCompletedStep: opportunity.furthestPhaseName ?? undefined,
  };
}
